import { CHARTS_2_URL } from 'features/charts-v2/paths'
import { ChartTypeV2 } from 'features/charts-v2/types/chart-types'
import type {
  Breakdown,
  ChartPanel,
} from 'features/charts-v2/types/filter-hash/v2'
import { FEEDBACK_URL } from 'features/feedback/paths'
import { KEYWORDS_URL } from 'features/keywords/paths'

export const focusFields = [
  'brand',
  'theme',
  'product_hierarchy',
  'source_type',
]

// Static lens → view-type word. Available immediately (unlike the backend
// `alias` on defaultFilterList), so the empty state stays stable during load.
export const LENS_VIEW_TYPE: Record<string, string> = {
  product_hierarchy: 'product',
  brand: 'brand',
  theme: 'theme',
  source_type: 'source',
}

// Static lens → plural display label, derived from LENS_VIEW_TYPE so the two
// can't drift. Used as a fallback when defaultFilterList hasn't loaded yet so
// the View dropdown / breadcrumb never show the raw lowercase field name.
// => { product_hierarchy: 'Products', brand: 'Brands', theme: 'Themes', source_type: 'Sources' }
export const LENS_LABEL: Record<string, string> = Object.fromEntries(
  Object.entries(LENS_VIEW_TYPE).map(([field, word]) => [
    field,
    `${word.charAt(0).toUpperCase()}${word.slice(1)}s`,
  ]),
)

const PRODUCT_BREAKDOWN: Breakdown = {
  kind: 'standard',
  id: 'product',
  grouped: false,
}
const THEME_BREAKDOWN: Breakdown = {
  kind: 'standard',
  id: 'theme',
  grouped: true,
}

// The "worst performing" chart a catalog detail page links to, as the v2 panel
// fields it sets on top of the page's filters: sentiment by one breakdown,
// lowest first. These are the values the backend's v1 -> v2 translation gave
// the `6_5_17_sentiment` chart this link used to select by v1 chart id.
const worstPerformingPanel = (breakdown: Breakdown): Partial<ChartPanel> => ({
  chartType: ChartTypeV2.BAR_HORIZONTAL,
  metrics: ['sentiment'],
  breakdowns: [breakdown],
  sortDirection: 'ASC',
})

export const nextStepsConstants = {
  theme: [
    {
      page: CHARTS_2_URL,
      title: 'View the worst performing Products for this Theme',
      filters: {
        values: [],
        searchQuery: [],
        searchCondition: [],
      },
      chartPanel: worstPerformingPanel(PRODUCT_BREAKDOWN),
    },
    {
      page: KEYWORDS_URL,
      title: 'View Full Keyword Breakdown',
      filters: {
        values: [],
        searchQuery: [],
        searchCondition: [],
      },
      dashboardControls: {},
    },
    {
      page: FEEDBACK_URL,
      title: 'Read Records that mention this Theme',
      filters: {
        values: [],
        searchQuery: [],
        searchCondition: [],
      },
      dashboardControls: {},
    },
  ],
  brand: [
    {
      page: CHARTS_2_URL,
      title: 'View the worst performing Products for this Brand',
      filters: {
        values: [],
        searchQuery: [],
        searchCondition: [],
      },
      chartPanel: worstPerformingPanel(PRODUCT_BREAKDOWN),
    },
    {
      page: KEYWORDS_URL,
      title: 'View Full Brand Keyword Breakdown',
      filters: {
        values: [],
        searchQuery: [],
        searchCondition: [],
      },
      dashboardControls: {},
    },
    {
      page: FEEDBACK_URL,
      title: 'Read Records for this Brand',
      filters: {
        values: [],
        searchQuery: [],
        searchCondition: [],
      },
      dashboardControls: {},
    },
  ],
  product_hierarchy: [
    {
      page: CHARTS_2_URL,
      title: 'View the worst performing Themes for this Product',
      filters: {
        values: [],
        searchQuery: [],
        searchCondition: [],
      },
      chartPanel: worstPerformingPanel(THEME_BREAKDOWN),
    },
    {
      page: KEYWORDS_URL,
      title: 'View Product Keyword Breakdown',
      filters: {
        values: [],
        searchQuery: [],
        searchCondition: [],
      },
      dashboardControls: {},
    },
    {
      page: FEEDBACK_URL,
      title: 'Read Records that mention this Product',
      filters: {
        values: [],
        searchQuery: [],
        searchCondition: [],
      },
      dashboardControls: {},
    },
  ],
  product: [
    {
      page: CHARTS_2_URL,
      title: 'View the worst performing Themes for this Product',
      filters: {
        values: [],
        searchQuery: [],
        searchCondition: [],
      },
      chartPanel: worstPerformingPanel(THEME_BREAKDOWN),
    },
    {
      page: KEYWORDS_URL,
      title: 'View Product Keyword Breakdown',
      filters: {
        values: [],
        searchQuery: [],
        searchCondition: [],
      },
      dashboardControls: {},
    },
    {
      page: FEEDBACK_URL,
      title: 'Read Records that mention this Product',
      filters: {
        values: [],
        searchQuery: [],
        searchCondition: [],
      },
      dashboardControls: {},
    },
  ],
  source_type: [
    {
      page: CHARTS_2_URL,
      title: 'View the worst performing Products for this Source',
      filters: {
        values: [],
        searchQuery: [],
        searchCondition: [],
      },
      chartPanel: worstPerformingPanel(PRODUCT_BREAKDOWN),
    },
    {
      page: KEYWORDS_URL,
      title: 'View Full Keyword Breakdown',
      filters: {
        values: [],
        searchQuery: [],
        searchCondition: [],
      },
      dashboardControls: {},
    },
    {
      page: FEEDBACK_URL,
      title: 'Read Records from this Source',
      filters: {
        values: [],
        searchQuery: [],
        searchCondition: [],
      },
      dashboardControls: {},
    },
  ],
}
